import React, { useState } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { GlobalStateContext } from "../context/global-context-provider";
import { modifyColor } from "../utils/helpers";
import ArrowUpRightLogo from "../images/arrow-up-right.png";
import UnstyledImage from "./image";
import { useLocation } from "@reach/router";

const NavLink = styled((props) => <Link {...props} />)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-size: 1rem;
  padding: 0;
  display: flex;
  align-items: center;
  &:hover {
    text-decoration: underline;
  }
`;
const HasChildren = styled.span`
  margin: 0 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-size: 1rem;
  padding: 0;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const StyledMenu = styled.ul`
  white-space: nowrap;
  display: flex;
  align-items: center;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  height: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: none;
  }
`;

const NavItem = styled.li`
  list-style-type: none;
  display: flex;
  margin-inline: 1.5rem;
`;
const SubNavItem = styled.li`
  list-style-type: none;
  position: relative;
  display: flex;
  flex-direction: column;
  padding-block: ${({ $isActive }) => ($isActive ? "0.5rem 1rem" : "1rem")};
  flex-wrap: wrap;
  @media (min-width: 1700px) {
    max-width: 250px;
  }

  > a {
    margin-bottom: 0.5rem;
  }
`;
const SubSubNavItem = styled(Link)`
  font-weight: normal;
  margin: 0 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-size: 1rem;
  padding: 5px 0;
  @media (hover: hover) {
    &:hover {
      text-decoration: underline;
    }
  }

  &.active-subnav {
    background-color: rgba(255, 87, 34, 0.1);
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.colors.primary};
    border-radius: 6px;
    padding: 0.75rem 1rem;
    display: inline-block;
    margin: 0 2rem;
    width: fit-content;
  }
`;
const UlSubLinks = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  max-width: 80%;
  width: 100%;
  padding: 2rem;
  gap: 0.7rem 1.5rem;
  justify-content: flex-start;
  @media (max-width: 1700px) {
    max-width: 70%;
  }
`;
const Dropdown = styled.div`
  max-height: 0;
  display: flex;
  overflow: hidden;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  position: absolute;
  top: 70px;
  left: 0;
  width: 100%;
  background-color: ${({ theme }) =>
    theme.colors.headerBackground || theme.colors.secondary};
  box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transition: all 0.5s ease;
  &.active {
    max-height: 1000px;
  }
`;

const SubNavLink = styled((props) => <Link {...props} />)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.headerLink || theme.colors.black};
  font-family: ${({ theme }) => theme.fontLinks.fontFamily};
  font-size: 1rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  font-weight: 700;
  @media (hover: hover) {
    span {
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &.active-subnav {
    background-color: rgba(255, 87, 34, 0.1);
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 600;
    border: 1px solid ${({ theme }) => theme.colors.primary};
    border-radius: 6px;
    padding: 0.5rem 1rem;
    margin: 0;
  }
`;
const ExtraLink = styled.div`
  padding-block: 1rem;
  a {
    color: var(--color-primary);
  }
`;
const CartQuantity = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  p {
    font-weight: 600;
    margin: 0 0 0 5px;
  }
`;
const ArrowImage = styled(UnstyledImage)`
  padding-left: 0.5rem;
`;
const ButtonStyle = styled(Link)`
  background-color: ${({ theme }) =>
    theme.colors.headerButton || theme.colors.primary};
  color: ${({ theme }) => theme.colors.headerButtonText || theme.colors.white};
  border: 2px solid
    ${({ theme }) => theme.colors.headerButton || theme.colors.primary};
  font-size: 1rem;
  line-height: initial;
  text-decoration: none;
  transition: 0.3s all ease-in-out;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 5px 10px 5px 10px;
  padding: 7.5px 45px;
  border-radius: 4px;

  @media (hover: hover) {
    &:hover {
      text-decoration: none;
      cursor: pointer;
      background-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || theme.colors.primary, -40)};
      border-color: ${({ theme }) =>
        modifyColor(theme.colors.headerButton || theme.colors.primary, -40)};
    }
  }
`;
const SubNavDescription = styled.span`
  font-size: 0.85rem;
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.primary : theme.colors.textLight || "#78797F"};
  margin-top: 0.25rem;
  line-height: 1.4;
  font-family: ${({ theme }) => theme.fontParagraphs.fontFamily};
  font-weight: 100;
  text-decoration: none;
  pointer-events: none;
`;

const StyledIcon = styled(({ dropdownOpen: dropdownOpenIgnored, ...rest }) => (
  <FontAwesomeIcon {...rest} />
))(({ dropdownOpen }) => ({
  transform: dropdownOpen ? "rotate(180deg)" : "none",
  transition: "all 0.3s ease-in-out",
}));

const Menu = ({ cartUrl, headerNav }) => {
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [totalQtyOfProducts, setTotalQtyOfProducts] = useState(0);
  const { cart } = React.useContext(GlobalStateContext);
  const dropdownRef = React.useRef();
  const location = useLocation();

  React.useEffect(() => {
    const handleEscape = (e) =>
      e.keyCode === 27 && setActiveDropdown(undefined);
    const handleClickOutside = (e) =>
      dropdownRef?.current &&
      !dropdownRef.current.contains(e.target) &&
      setActiveDropdown(undefined);
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keyup", handleEscape);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keyup", handleEscape);
    };
  }, []);

  const openDropdown = (id) => {
    if (!activeDropdown || activeDropdown != id) {
      setActiveDropdown(id);
    } else {
      setActiveDropdown(null);
    }
  };

  React.useEffect(() => {
    setTotalQtyOfProducts(
      cart.reduce((totalQtyOfProducts, item) => {
        return totalQtyOfProducts + item.quantity;
      }, 0)
    );
  }, [cart]);

  const [navItems] = React.useState(() =>
    headerNav?.menuItems.nodes
      .filter((node) => node.parentId === null)
      .map((node) => {
        return {
          id: node.id,
          url: node.url,
          title: node.label,
          description: node.description,
          titleAttribute: node.title,
          isButton: node.cssClasses.includes("button"),
          subLinks: node.childItems?.nodes?.map((node) => ({
            id: node.id,
            url: node.url,
            title: node.label,
            description: node.description,
            titleAttribute: node.title,
            subLinks: node.childItems?.nodes?.map((node) => ({
              id: node.id,
              url: node.url,
              title: node.label,
            })),
          })),
        };
      })
  );

  const [menuItems] = React.useState(() =>
    navItems?.filter((item) => !item.isButton)
  );
  const [buttons] = React.useState(() =>
    navItems?.filter((item) => item.url != "/app/login" && item.isButton)
  );

  return (
    <StyledMenu ref={dropdownRef}>
      {menuItems?.map((node) => (
        <NavItem key={node.id}>
          {node.subLinks?.length > 0 ? (
            <HasChildren
              activeStyle={{ fontWeight: "bold" }}
              onClick={() => openDropdown(node.id)}
              style={{
                textDecoration:
                  activeDropdown === node.id ? "underline" : "none",
              }}
            >
              {node.title}
              <StyledIcon
                icon={faAngleDown}
                size="xs"
                style={{ marginLeft: "10px" }}
                dropdownOpen={activeDropdown === node.id}
              />
            </HasChildren>
          ) : node.url === cartUrl ? (
            <NavLink to={node.url}>
              <CartQuantity>
                <FontAwesomeIcon size="1x" icon={faShoppingCart} />
                {cart.length > 0 ? <p>{totalQtyOfProducts}</p> : ""}
              </CartQuantity>
            </NavLink>
          ) : (
            <NavLink to={node.url} activeStyle={{ fontWeight: "bold" }}>
              {node.title}
            </NavLink>
          )}
          <Dropdown className={activeDropdown === node.id ? "active" : ""}>
            {activeDropdown === node.id && node.subLinks?.length > 0 && (
              <>
                <UlSubLinks>
                  {node.subLinks.map((item) => (
                    <SubNavItem
                      key={item.id}
                      $isActive={
                        location.pathname === item.url.replace(/\/$/, "") ||
                        location.pathname.startsWith(
                          item.url.replace(/\/$/, "")
                        )
                      }
                    >
                      <SubNavLink to={item.url} activeClassName="active-subnav">
                        <span> {item.title}</span>
                        {item.description && (
                          <SubNavDescription
                            $isActive={
                              location.pathname ===
                                item.url.replace(/\/$/, "") ||
                              location.pathname.startsWith(
                                item.url.replace(/\/$/, "")
                              )
                            }
                          >
                            {item.description}
                          </SubNavDescription>
                        )}
                      </SubNavLink>

                      {item.subLinks?.length > 0 &&
                        item.subLinks.map((item) => (
                          <SubSubNavItem
                            to={item.url}
                            key={item.id}
                            activeClassName="active-subnav"
                          >
                            {item.title}
                          </SubSubNavItem>
                        ))}
                    </SubNavItem>
                  ))}
                  <ExtraLink>
                    <NavLink to={node.url} style={{ fontWeight: "700" }}>
                      {node.titleAttribute}
                      <ArrowImage src={ArrowUpRightLogo} />
                    </NavLink>
                    {node.description && (
                      <SubNavDescription>{node.description}</SubNavDescription>
                    )}
                  </ExtraLink>
                </UlSubLinks>
              </>
            )}
          </Dropdown>
        </NavItem>
      ))}
      <NavItem style={{ marginLeft: "25px" }}>
        {buttons?.map((button) => (
          <ButtonStyle key={button.id} to={button.url}>
            {button.title}
          </ButtonStyle>
        ))}
      </NavItem>
    </StyledMenu>
  );
};

Menu.propTypes = {
  cartUrl: PropTypes.string,
  headerNav: PropTypes.object,
};

export default Menu;
